import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { isEqual } from 'lodash';
import moment from 'moment';
import { injectIntl } from 'react-intl';

import { DateTimeInput as SharedDateTimeInput } from 'shared/components';
import requestStatuses from 'modules/common/constants/requestsStatuses';

import { isErrorCode, checkErrorStatus } from './checkError';
import InputProxy from './InputProxy';

const propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.any,
    name: PropTypes.string,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pristine: PropTypes.bool,
    submitFailed: PropTypes.bool,
  }).isRequired,
  errorMessagesMap: PropTypes.object,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  label: PropTypes.object,
  rootClassName: PropTypes.string,
  shouldInitTime: PropTypes.bool,
  disableTimeSelection: PropTypes.bool,
  items: PropTypes.array,
  getOptions: PropTypes.func,
};

const defaultProps = {
  errorMessagesMap: {},
  defaultValue: null,
  label: null,
  rootClassName: '',
  placeholder: undefined,
  shouldInitTime: true,
  disableTimeSelection: false,
  items: null,
  getOptions: null,
};

const DateTimeInput = (props) => {
  const {
    input,
    meta: { error },
    defaultValue,
    errorMessagesMap,
    onDateChange,
    timeValue,
    changeTimeValue,
    disableTimeSelection,
    timeFieldMeta,
    setTimeFieldMeta,
    ...rest
  } = props;

  const errorMessage = !isErrorCode(error)
    ? { defaultMessage: error, id: 'default' }
    : error && errorMessagesMap[error];

  const errorTimeMessage = !disableTimeSelection && timeFieldMeta
    ? !isErrorCode(timeFieldMeta.error)
      ? { defaultMessage: timeFieldMeta.error, id: 'default' }
      : timeFieldMeta.error && errorMessagesMap[timeFieldMeta.error]
    : null;

  return (
    <div>
      <SharedDateTimeInput
        { ...input }
        { ...rest }
        testId={ input.name }
        status={ checkErrorStatus(props.meta)
          ? requestStatuses.failure
          : requestStatuses.default
        }
        timeStatus={ timeFieldMeta && checkErrorStatus(timeFieldMeta)
          ? requestStatuses.failure
          : requestStatuses.default
        }
        date={ !input.value ? defaultValue : input.value }
        setDate={ onDateChange }
        time={ timeValue }
        setTime={ changeTimeValue }
        tooltipMessage={ checkErrorStatus(props.meta) ? errorMessage : null }
        timeTooltipMessage={ timeFieldMeta && checkErrorStatus(timeFieldMeta) ? errorTimeMessage : null }
        disableTimeSelection={ disableTimeSelection }
      />
      <If condition={ !disableTimeSelection }>
        <Field
          name={ `${ input.name }-time` }
          component={ InputProxy }
          inputValue={ timeValue }
          proxyFieldMeta={ setTimeFieldMeta }
        />
      </If>
    </div>
  );
};

DateTimeInput.propTypes = propTypes;
DateTimeInput.defaultProps = defaultProps;

export default compose(
  injectIntl,
  withHandlers({
    onDateChange: ({ input: { onChange }, timeValue: timeValuePrev }) => (value, currTimeValue) => {
      const offset = moment(value).utcOffset();
      const timeValue = currTimeValue || timeValuePrev;
      if (!timeValue) return onChange(value);

      let [hours, minutes] = timeValue.value.split(':');
      if (Number(timeValue.key) >= 13) hours = Number(hours) + 12;
      if (Number(timeValue.key) < 1) hours = 0;
      minutes = minutes.split(' ')[0];

      const date = moment(value).utcOffset(offset).startOf('day').add({ hours, minutes });
      onChange(date);
    },
  }),
  withStateHandlers({ timeValue: null, initialized: false, timeFieldMeta: null }, {
    changeTimeValue: (state, { input: { value: dateValue }, onDateChange }) => (value) => {
      if (dateValue) onDateChange(dateValue, value);

      return { timeValue: value };
    },
    setInit: () => () => ({ initialized: true }),
    setTimeFieldMeta: () => (error) => ({ timeFieldMeta: error }),
  }),
  lifecycle({
    componentDidUpdate() {
      const { intl, initialized, shouldInitTime = defaultProps.shouldInitTime, setInit, changeTimeValue, items, getOptions, optionsInterval = 0.5, input: { value } } = this.props;

      if (!initialized && shouldInitTime && value) {
        const PM = intl.formatMessage({ id: 'General.PM', defaultMessage: '** PM' });
        const AM = intl.formatMessage({ id: 'General.AM', defaultMessage: '** AM' });
        const date = `${ moment(value).format('h:mm') } ${ moment(value).format('HH') >= 12 ? PM : AM }`;
        const list = items || (getOptions
          ? getOptions(optionsInterval)
          : [{ key: moment(value).format('HH') }]);

        const selectedItem = list.find(item => item.value === date);

        if (selectedItem) {
          setInit();
          changeTimeValue(selectedItem);
        }
      }
    },
    shouldComponentUpdate(nextProps) {
      return !isEqual(this.props.input, nextProps.input)
        || !isEqual(this.props.meta, nextProps.meta)
        || this.props.disabled !== nextProps.disabled
        || this.props.loading !== nextProps.loading
        || this.props.timeFieldMeta !== nextProps.timeFieldMeta
        || this.props.timeValue !== nextProps.timeValue;
    },
  }),
)(DateTimeInput);
